import styled from "@emotion/styled";
import { css } from "@emotion/react";
import {
  spacing,
  shadow,
  breakpoints,
  fontSize,
  fontWeight,
  borderRadius,
  letterSpacing,
  footer,
  nav,
  interactive,
  brand,
  status,
  surface,
  text,
  lineHeight,
  duration,
} from "@atlas-int/tokens";
import { useState } from "react";
import { Link } from "react-router";

import { H4, SubSectionHeader } from "@atlas-int/ui";
import { Quote } from "./Utils";
import IntroCards from "./IntroCards";
import { breakpointSmall, PageSection, ArrowIcon } from "./Utils";
import VizHubIcon from "../../assets/images/VizHubIcon.svg";
import HornIcon from "./img/horn.svg";
import NewYorkTimesLogo from "./img/new-york-times.svg";
import AtlasBookJacket from "./img/atlas_bookjacket.svg";
import VizHubPattern from "./img/VizHubPattern.png";
import MetroverseLogoImage from "./img/metroverse-logo.png";
import BloombergWhiteLogo from "./img/Bloomberg_white.png";
import FinancialTimesWhiteLogo from "./img/financialtimes_white.png";
import NprWhiteLogo from "./img/npr_white.png";
import BookPatternImage from "./img/book-pattern_image.webp";
import TwitterFeed from "./TwitterEmbed";
import { googleAnalyticsCustomEvent } from "../../sharedUtilities/analytics";

const containerMobileSpacing = spacing[10];
const containerDesktopSpacing = "100px";

const Container = styled.div`
  line-height: ${lineHeight.normal};
  margin: 0 auto;
  margin-top: -${spacing[16]};
  display: grid;
  grid-template-columns: ${containerMobileSpacing} auto ${containerMobileSpacing};

  @media (min-width: ${breakpoints.lg}) {
    grid-template-columns: ${containerDesktopSpacing} auto ${containerDesktopSpacing};
  }
`;

const InvertedSubSectionHeader = styled(SubSectionHeader)`
  color: ${text.onDark};
  margin: 1em 0;
`;

const TestimonyAndNewsSection = styled(PageSection)`
  grid-column: span 3;
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: 0.5rem;
  grid-column-gap: 0.5rem;
  grid-template-rows: 3rem repeat(auto, 4);
  color: ${text.onDark};
  background-color: ${nav.background};
  padding: ${spacing[10]} ${containerMobileSpacing};

  @media (min-width: ${breakpoints.lg}) {
    padding: ${spacing[10]} ${containerDesktopSpacing};
  }

  @media (min-width: ${breakpointSmall}px) {
    grid-template-columns: 1fr 2fr 2fr;
    grid-column-gap: 3rem;
  }
`;

const HighlightedSection = styled(PageSection)`
  grid-column: span 3;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 2rem;
  padding: 0 ${containerMobileSpacing};

  @media (min-width: ${breakpoints.lg}) {
    padding: 3rem ${containerDesktopSpacing};
  }

  @media (max-width: ${breakpoints.md}) {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;
  }
`;

const VizHubSection = styled(HighlightedSection)`
  background-image: url(${VizHubPattern});
  background-size: cover;
  background-position: center;
`;

const NewsItem = styled.a`
  display: grid;
  grid-template-columns: 1fr;
  row-gap: 0.5rem;
  align-items: center;
  color: inherit;
  text-decoration: none;

  @media (min-width: ${breakpointSmall}px) {
    grid-column: 1 / 3;
    grid-template-columns: subgrid;
  }
`;

const NewsLogoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  grid-column: 1;
  margin: 0 auto;
  margin-top: 2.5rem;
  max-width: 180px;
  text-decoration: none;

  img {
    width: 100%;
  }

  @media (min-width: ${breakpointSmall}px) {
    margin-top: 0;
  }
`;

const NewsLogoContainer1 = styled(NewsLogoContainer)`
  @media (min-width: ${breakpointSmall}px) {
    max-width: 80%;
  }
`;
const NewsLogoContainer4 = styled(NewsLogoContainer)`
  @media (min-width: ${breakpointSmall}px) {
    max-width: 40%;
  }
`;
const NewsTitleContainer = styled.div`
  display: block;
  font-size: ${fontSize["2xl"]};
  grid-column: 1;
  text-align: center;
  text-decoration: none;
  color: ${text.onDark};
  font-weight: ${fontWeight.normal};

  @media (min-width: ${breakpointSmall}px) {
    grid-column: 2;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    text-align: left;
  }
`;

const TestimonialContainer = styled.div`
  display: flex;
  flex-direction: column;
  grid-column: 1;

  @media (min-width: ${breakpointSmall}px) {
    grid-column: 3;
    grid-row: 2 / 6;
  }
`;

const TestimonyQuoteContainer = styled.div`
  justify-content: flex-start;
`;
const TestimonyQuote = styled(Quote)`
  font-size: ${fontSize["2xl"]};
  color: ${text.onDark};
`;

const newsSectionStyle = css`
  letter-spacing: ${letterSpacing.wide};
  margin-bottom: 8vh;
  grid-column: 1;
`;

const NewNewsTitle = styled.div`
  ${newsSectionStyle}
  grid-row: 1;
  grid-column: 1 / 4;
  text-align: center;
  border-bottom: 1px solid ${text.onDark};
  display: block;
  margin: 0 auto;
  width: 100%;
`;

const ResourcesContainer = styled(PageSection)`
  display: grid;
  grid-row-gap: 5%;
  margin-bottom: 10vh;

  @media (min-width: ${breakpointSmall}px) {
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 5%;
    margin-bottom: 5vh;
  }
`;

const buttonColor = text.secondary;
const CallToAction = styled.div`
  display: block;
  border: 1px solid ${buttonColor};
  padding: 0.5rem;
  background-color: ${surface.raised};
  color: ${buttonColor};
  float: right;
  border-radius: ${borderRadius.pill};
  text-transform: uppercase;
  position: absolute;
  bottom: 1rem;
  right: 1rem;

  a {
    color: inherit;
    text-decoration: none;
  }

  &:hover {
    color: ${text.onDark};
    background-color: ${buttonColor};
    cursor: pointer;
  }
`;
const EBookContainer = styled.div`
  background-color: ${surface.raised};
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  background-size: 100% 100%;
  box-shadow: 0px 0px 4px 2px rgba(0, 0, 0, 0.25);

  @media (min-width: ${breakpointSmall}px) {
    grid-row: 1;
    grid-column: 2;
  }
`;

const BookImageContainer = styled.div`
  display: block;
  margin: 0 auto;
  width: 100%;
  height: 100%;
  padding: 15%;
  background-color: rgba(255, 255, 255, 0.5);
  box-shadow: ${shadow.lg};
  box-sizing: border-box;
`;

const VizHubTitle = styled.div`
  padding: 1rem 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-bottom: 2rem;
`;
const VizHubLogo = styled.div`
  width: 100%;
  margin-bottom: 3rem;

  img {
    width: 100%;
    height: 100%;

    .cls-1 {
      fill: ${status.error};
      opacity: 0.9;
    }

    .cls-2 {
      fill: ${brand.dark};
    }
  }
`;
const MetroverseLogo = styled.img`
  width: 100%;
  margin-bottom: 3rem;
`;
const GoToButton = styled.a`
  display: inline-flex;
  align-items: center;
  background-color: ${surface.raised};
  padding: 0.5rem 1.2rem 0.5rem 2rem;
  font-size: ${fontSize["2xl"]};
  border-radius: ${borderRadius.pill};
  border: solid 1px ${brand.dark};
  color: ${brand.dark};
  text-transform: uppercase;
  text-decoration: none;
  font-weight: ${fontWeight.semibold};
  transition:
    background-color ${duration.base} ease,
    color ${duration.base} ease,
    border-color ${duration.base} ease;

  svg {
    width: 0.7rem;
    height: 0.7rem;
    margin-left: 0.7rem;
    fill: ${status.error};
    transition: fill ${duration.base} ease;
  }

  &:hover {
    background-color: ${brand.dark};
    color: ${text.onDark};
    border-color: ${brand.dark};

    svg {
      fill: ${text.onDark};
    }
  }
`;
const VizHubDescription = styled.div`
  font-size: ${fontSize["2xl"]};
  line-height: ${lineHeight.loose};
  background-color: ${surface.raised};
  color: ${text.primary};
  padding: 1.5rem 2rem;
  border-left: solid 4px ${status.gold};
`;

const MetroverseDescription = styled(VizHubDescription)`
  background-color: ${surface.muted};
`;

const MapContainer = styled.div`
  display: flex;
  flex-direction: column;
  grid-column: span 3;
  justify-content: center;
  align-items: center;
  width: 100%;
  position: relative;
  margin: 5vh 0
  padding: 0 ${containerMobileSpacing};
  box-sizing: border-box;

  @media (min-width: ${breakpoints.lg}) {
    padding: 0 ${containerDesktopSpacing};
  }
`;

const MapHeader = styled.h2`
  align-self: flex-start;
  font-weight: ${fontWeight.normal};
  font-size: ${fontSize["4xl"]};
  color: ${text.primary};
  /* Pin the top margin to the old <h1> default (0.67em) so demoting this
     to <h2> — whose browser default is 0.83em — doesn't nudge the layout. */
  margin-top: 0.67em;
  margin-bottom: 1rem;
`;

const MapDescription = styled.p`
  font-weight: ${fontWeight.normal};
  font-size: ${fontSize.lg};
  letter-spacing: ${letterSpacing.tight};
  color: ${text.primary};
  opacity: 1;
  margin-bottom: 2rem;

  b {
    font-weight: ${fontWeight.semibold};
    color: ${footer.bg};
  }
`;

const MapIframe = styled.iframe`
  border: none;
  width: 100%;
  height: 450px;
  max-width: 100%;
  aspect-ratio: 950 / 450;

  @media (max-width: ${breakpoints.sm}) {
    height: 56.25vw; // 16:9 aspect ratio
    max-width: calc(100vw - 2 * ${containerMobileSpacing});
  }
`;

const MethodologyCalloutSection = styled.div`
  grid-column: span 3;
  background: rgba(36, 55, 68, 0.3);
  padding: ${spacing[8]} ${containerMobileSpacing};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[8]};
  flex-wrap: wrap;

  @media (min-width: ${breakpoints.lg}) {
    padding: ${spacing[8]} ${containerDesktopSpacing};
  }
`;

const MethodologyCalloutContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[8]};
`;

const MethodologyCalloutIcon = styled.img`
  width: 34px;
  height: auto;
`;

const MethodologyCalloutText = styled.span`
  color: ${text.primary};
  font-size: ${fontSize.xl};
  font-weight: ${fontWeight.semibold};
`;

const MethodologyCalloutButton = styled(Link)`
  padding: ${spacing[2]} 14px;
  border-radius: ${borderRadius.base};
  border: 1px solid ${interactive.default};
  color: ${interactive.default};
  font-size: ${fontSize.lg};
  text-decoration: none;
  background: transparent;

  &:hover {
    background: ${interactive.default};
    color: ${text.onDark};
  }
`;

const LandingPageMap = () => {
  return (
    <MapContainer>
      <MapHeader>Atlas in Action</MapHeader>
      <MapDescription>
        The Atlas has reached more than 950,000 people in 228 countries. See the
        real-world impact of The Atlas as it's used in global trainings,
        strategic development projects, executive education, and more.{" "}
        <b>
          Click on the highlighted countries on the map to learn more about
          these projects.
        </b>
      </MapDescription>
      <MapIframe
        src="https://cid-harvard.github.io/landing-map/index.html"
        title="Highlighted World Map"
      />
    </MapContainer>
  );
};

const Content = () => {
  const [triggeredSections, setTriggeredSections] = useState<Set<string>>(
    new Set(),
  );

  const createSectionObserver =
    (sectionId: string) => (element: HTMLDivElement | null) => {
      if (element && !triggeredSections.has(sectionId)) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                googleAnalyticsCustomEvent(
                  "scroll",
                  `section${sectionId}`,
                  "home",
                  "",
                );
                setTriggeredSections((prev) => new Set([...prev, sectionId]));
                observer.disconnect();
              }
            });
          },
          sectionId === "3"
            ? { threshold: 0.1, rootMargin: "0px 0px 100px 0px" }
            : { threshold: 0.5 },
        );

        observer.observe(element);
      }
    };

  const nytLink = `https://www.nytimes.com/2026/04/13/business/economy/iran-imports-exports-china.html`;
  const bloombergLink =
    /* tslint:disable-next-line:max-line-length */
    `https://www.bloomberg.com/opinion/articles/2019-09-18/repo-market-spike-carries-echoes-of-2007-2008-crisis-k0oqoebp`;
  const ftLink = `https://www.ft.com/content/7d51a630-a3de-4cc7-9f5f-0f3e7f0d305a?syn-25a6b1a6=1`;
  const nprLink = `https://www.npr.org/sections/money/2018/02/28/589609380/episode-827-three-indicators`;

  return (
    <Container>
      <MethodologyCalloutSection>
        <MethodologyCalloutContent>
          <MethodologyCalloutIcon src={HornIcon} alt="" />
          <MethodologyCalloutText>
            We've upgraded our trade data. Discover the Growth Lab's new
            cleaning methodology
          </MethodologyCalloutText>
        </MethodologyCalloutContent>
        <MethodologyCalloutButton to="/trade-data-methodology">
          Learn More
        </MethodologyCalloutButton>
      </MethodologyCalloutSection>

      <IntroCards />

      <VizHubSection ref={createSectionObserver("1")}>
        <VizHubTitle>
          <VizHubLogo>
            <img src={VizHubIcon} alt="Viz Hub Logo" />
          </VizHubLogo>
          <div>
            <GoToButton
              href="https://growthlab.app/"
              target="_blank"
              rel="noopener"
              onClick={() => {
                googleAnalyticsCustomEvent(
                  "btn",
                  "click",
                  "gotovizhub",
                  "home",
                );
              }}
            >
              Go to Viz Hub
              <ArrowIcon />
            </GoToButton>
          </div>
        </VizHubTitle>
        <VizHubDescription>
          The Atlas of Economic Complexity is part of Harvard's Growth Lab Viz
          Hub, a portfolio of award-winning, interactive visualizations powered
          by the Growth Lab's research and insights. Check out the Viz Hub to
          learn more about concepts found in the Atlas along with our other
          free, online tools and software packages.
        </VizHubDescription>
      </VizHubSection>
      <HighlightedSection ref={createSectionObserver("2")}>
        <VizHubTitle>
          <MetroverseLogo src={MetroverseLogoImage} alt="Metroverse Logo" />
          <div>
            <GoToButton
              href="https://metroverse.hks.harvard.edu/"
              target="_blank"
              rel="noopener"
              onClick={() => {
                googleAnalyticsCustomEvent(
                  "btn",
                  "click",
                  "gotometroverse",
                  "home",
                );
              }}
            >
              Go to Metroverse
              <ArrowIcon />
            </GoToButton>
          </div>
        </VizHubTitle>
        <MetroverseDescription>
          Metroverse is the Growth Lab's urban economy navigator covering over
          1000 cities across the world. By combining cutting-edge research,
          high-resolution datasets and stunning data visualizations, Metroverse
          makes visible what a city is good at today to help understand what it
          can become tomorrow.
        </MetroverseDescription>
      </HighlightedSection>
      <TestimonyAndNewsSection ref={createSectionObserver("2")}>
        <NewNewsTitle>
          <InvertedSubSectionHeader as="h2">
            In the News
          </InvertedSubSectionHeader>
        </NewNewsTitle>

        <NewsItem href={nytLink} target="_blank" rel="noopener">
          <NewsLogoContainer1>
            <img src={NewYorkTimesLogo} alt="New York Times Logo" />
          </NewsLogoContainer1>
          <NewsTitleContainer>
            How Iran, Suffering Under Sanctions, Diversified Its Economy
          </NewsTitleContainer>
        </NewsItem>

        <NewsItem href={ftLink} target="_blank" rel="noopener">
          <NewsLogoContainer1>
            <img src={FinancialTimesWhiteLogo} alt="Financial Times Logo" />
          </NewsLogoContainer1>
          <NewsTitleContainer>
            China Shock 2.0: The Flood of High-Tech Goods That Will Change the
            World
          </NewsTitleContainer>
        </NewsItem>

        <NewsItem href={bloombergLink} target="_blank" rel="noopener">
          <NewsLogoContainer1>
            <img src={BloombergWhiteLogo} alt="Bloomberg Logo" />
          </NewsLogoContainer1>
          <NewsTitleContainer>
            Markets Are Starting to Play a Haunting 2007 Tune
          </NewsTitleContainer>
        </NewsItem>

        <NewsItem href={nprLink} target="_blank" rel="noopener">
          <NewsLogoContainer4>
            <img src={NprWhiteLogo} alt="NPR Logo" />
          </NewsLogoContainer4>
          <NewsTitleContainer>
            Planet Money: The Three Indicators
          </NewsTitleContainer>
        </NewsItem>

        <TestimonialContainer>
          <TestimonyQuoteContainer>
            <TestimonyQuote>
              "This is like adding the Periodic Table to chemistry. It organizes
              knowledge in a way that helps everyone: from student to journalist
              to policy maker to investors to economic experts."
            </TestimonyQuote>
            <H4>
              Lant Pritchett, RISE Research Director, University of Oxford
            </H4>
          </TestimonyQuoteContainer>

          <TestimonyQuoteContainer>
            <TestimonyQuote>
              "If you like data visualizations and are interested in analyzing
              trade flows and the sectoral composition of an economy, you can't
              beat this website from Harvard."
            </TestimonyQuote>
            <H4>The Guardian</H4>
          </TestimonyQuoteContainer>

          <TestimonyQuoteContainer>
            <TestimonyQuote>
              "...fascinating research done by the Center for International
              Development at Harvard University, which has a successful record
              of identifying which countries are positioned to grow."
            </TestimonyQuote>
            <H4>Financial Times</H4>
          </TestimonyQuoteContainer>
        </TestimonialContainer>
      </TestimonyAndNewsSection>

      <LandingPageMap />

      <ResourcesContainer ref={createSectionObserver("3")}>
        <TwitterFeed />
        <EBookContainer
          style={{
            backgroundImage: `url('${BookPatternImage}')`,
          }}
        >
          <BookImageContainer>
            <img
              alt="Atlas of Economic Complexity Book Cover"
              src={AtlasBookJacket}
            />
          </BookImageContainer>
          <CallToAction>
            <a
              href="https://growthlab.hks.harvard.edu/publication/the-atlas-of-economic-complexity-mapping-paths-to-prosperity-2/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                googleAnalyticsCustomEvent(
                  "btn",
                  "click",
                  "downloadbook",
                  "home",
                );
              }}
            >
              Download the 2013 Edition
            </a>
          </CallToAction>
        </EBookContainer>
      </ResourcesContainer>
    </Container>
  );
};

export default Content;
